import {
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import React, { useState } from "react";
import { getCategoryById, getImportItemById, getImportsByCategory } from "../../../services/Import";
import InputField from "../../../FormComponents/InputField";
import BillWritableTable from "../../../components/BillWritableTable";
import FormAlert from "../../../components/FormAlert";
import ItemSelectingPopup from "../../../components/ItemSelectingPopup";
import Im_CategorySelectingPopup from "../../../components/Im_CategorySelectingPopup";
import CategoryItemsViewDialog from "../../../components/CategoryItemsViewDialog";
import SubOrderProductValidation from "../../../Validation/SubOrderProductValidation";
import { addSubOrderProduct } from "../../../services/SubOrderProduct";

function SubOrderProductCreate({ setOpenForm }) {
  const [values, setValues] = useState({
    _productName: "",
    _subOrderItemBodyList: [],
    _note: "",
  });

  const resetValues = () => {
    closeAlert();
    setSelectedInputItems([]);
    setSelectedOutputItems([]);
    setSelectedInputRows([]);
    setSelectedOutputRows([]);
    setSelectedInputCategories([]);
    setSelectedOutputCategories([]);
    setInputCategoryRows([]);
    setOutputCategoryRows([]);
    setError({});
    setValues({ _productName: "", _subOrderItemBodyList: [], _note: "" });
  };

  const [displayAlert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({ type: "", message: "" });

  const closeAlert = () => {
    setAlert(false);
    setAlertData({ type: "", message: "" });
  };

  // import item popup state
  const [openInputTable, setOpenInputTable] = useState(false);
  const [openOutputTable, setOpenOutputTable] = useState(false);

  // import category popup state — input
  const [openInputCategoryTable, setOpenInputCategoryTable] = useState(false);
  const [selectedInputCategories, setSelectedInputCategories] = useState([]);
  const [inputCategoryRows, setInputCategoryRows] = useState([]);

  // import category popup state — output
  const [openOutputCategoryTable, setOpenOutputCategoryTable] = useState(false);
  const [selectedOutputCategories, setSelectedOutputCategories] = useState([]);
  const [outputCategoryRows, setOutputCategoryRows] = useState([]);

  // category items view dialog
  const [categoryViewOpen, setCategoryViewOpen] = useState(false);
  const [categoryViewName, setCategoryViewName] = useState("");
  const [categoryViewItems, setCategoryViewItems] = useState([]);

  const [error, setError] = useState({});

  const handleChange = (name, val) => {
    setValues({ ...values, [name]: val });
    if (error[name]) setError({ ...error, [name]: "" });
  };

  const itemColumns = [
    { id: "item", label: "Item", minWidth: 100, editable: false },
    { id: "brand", label: "Brand", minWidth: 100, editable: false },
    { id: "qty", label: "Qty", minWidth: 100, editable: true, type: "number", isDecimal: false },
    { id: "orderUnit", label: "Order Unit", minWidth: 100, editable: true },
    { id: "unitType", label: "Unit", minWidth: 100, editable: false },
    { id: "itemUintsPerOrderUnit", label: "Item Units Amount", minWidth: 100, editable: true, type: "number", isDecimal: true },
  ];

  const [selectedInputItems, setSelectedInputItems] = useState([]);
  const [selectedInputRows, setSelectedInputRows] = useState([]);
  const [selectedOutputItems, setSelectedOutputItems] = useState([]);
  const [selectedOutputRows, setSelectedOutputRows] = useState([]);
  const [rows, setRows] = useState([]);

  const [writeable_page] = useState(0);
  const [writeable_rowsPerPage] = useState(100);

  function createDataForSelectedTable(
    item, brand, qty, orderUnit, unitType, itemUintsPerOrderUnit,
    importId, existingQty, importCategoryId = null
  ) {
    return { item, brand, qty, orderUnit, unitType, itemUintsPerOrderUnit, importId, existingQty, importCategoryId };
  }

  // ── item close handlers ──────────────────────────────────────────────────
  const handleCloseInputTable = async () => {
    setOpenInputTable(false);
    const existingItemsById = Object.fromEntries(
      selectedInputRows.filter((r) => r.importId != null).map((r) => [r.importId, r])
    );
    const result = selectedInputRows.filter((r) => r.importCategoryId != null);
    for (const importId of selectedInputItems) {
      if (existingItemsById[importId]) {
        result.push(existingItemsById[importId]);
      } else {
        const importItem = await getImportItemById(importId);
        result.push(createDataForSelectedTable(
          importItem.itemName + "(" + importItem.product_code + ")",
          importItem.brand, 0.0, importItem.unitType, importItem.unitType, 1.0,
          importItem.importId, importItem.qty
        ));
      }
    }
    setSelectedInputRows(result);
    if (error._inputItems) setError({ ...error, _inputItems: "" });
  };

  const handleCloseOutputTable = async () => {
    setOpenOutputTable(false);
    const existingItemsById = Object.fromEntries(
      selectedOutputRows.filter((r) => r.importId != null).map((r) => [r.importId, r])
    );
    const result = selectedOutputRows.filter((r) => r.importCategoryId != null);
    for (const importId of selectedOutputItems) {
      if (existingItemsById[importId]) {
        result.push(existingItemsById[importId]);
      } else {
        const importItem = await getImportItemById(importId);
        result.push(createDataForSelectedTable(
          importItem.itemName + "(" + importItem.product_code + ")",
          importItem.brand, 0.0, importItem.unitType, importItem.unitType, 1.0,
          importItem.importId, importItem.qty
        ));
      }
    }
    setSelectedOutputRows(result);
    if (error._outputItems) setError({ ...error, _outputItems: "" });
  };

  // ── category close handlers ──────────────────────────────────────────────
  const handleCloseInputCategoryTable = async () => {
    setOpenInputCategoryTable(false);
    const existingCatsById = Object.fromEntries(
      selectedInputRows.filter((r) => r.importCategoryId != null).map((r) => [r.importCategoryId, r])
    );
    const result = selectedInputRows.filter((r) => r.importId != null);
    for (const catId of selectedInputCategories) {
      if (existingCatsById[catId]) {
        result.push(existingCatsById[catId]);
      } else {
        const category = await getCategoryById(catId);
        result.push(createDataForSelectedTable(
          category.category, "", 0.0, "", "", 1.0, null, null, category.cat_id
        ));
      }
    }
    setSelectedInputRows(result);
    if (error._inputItems) setError({ ...error, _inputItems: "" });
  };

  const handleCloseOutputCategoryTable = async () => {
    setOpenOutputCategoryTable(false);
    const existingCatsById = Object.fromEntries(
      selectedOutputRows.filter((r) => r.importCategoryId != null).map((r) => [r.importCategoryId, r])
    );
    const result = selectedOutputRows.filter((r) => r.importId != null);
    for (const catId of selectedOutputCategories) {
      if (existingCatsById[catId]) {
        result.push(existingCatsById[catId]);
      } else {
        const category = await getCategoryById(catId);
        result.push(createDataForSelectedTable(
          category.category, "", 0.0, "", "", 1.0, null, null, category.cat_id
        ));
      }
    }
    setSelectedOutputRows(result);
    if (error._outputItems) setError({ ...error, _outputItems: "" });
  };

  // ── remove handlers ──────────────────────────────────────────────────────
  const removeInputCategoryRow = (catId) => {
    setSelectedInputRows((prev) => prev.filter((r) => r.importCategoryId !== catId));
    setSelectedInputCategories((prev) => prev.filter((id) => id !== catId));
  };

  const removeOutputCategoryRow = (catId) => {
    setSelectedOutputRows((prev) => prev.filter((r) => r.importCategoryId !== catId));
    setSelectedOutputCategories((prev) => prev.filter((id) => id !== catId));
  };

  // ── view items handler ───────────────────────────────────────────────────
  const handleViewCategoryItems = async (catId, catName) => {
    const items = await getImportsByCategory(catId);
    setCategoryViewName(catName);
    setCategoryViewItems(Array.isArray(items) ? items : []);
    setCategoryViewOpen(true);
  };

  // ── submit ───────────────────────────────────────────────────────────────
  const submitValue = async () => {
    const validationErrors = SubOrderProductValidation(selectedInputRows, selectedOutputRows);
    setError(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const buildItem = (row, itemType) => {
        const item = {
          itemQty: parseFloat(row.qty) || 0,
          itemType,
          itemUnit: row.unitType,
          prodUnitsForItemUnit: parseFloat(row.itemUintsPerOrderUnit) || 1,
        };
        if (row.importCategoryId != null) {
          item.importCategoryId = row.importCategoryId;
        } else {
          item.importId = row.importId;
        }
        return item;
      };

      const subOrderItems = [
        ...selectedInputRows.map((r) => buildItem(r, "INPUT")),
        ...selectedOutputRows.map((r) => buildItem(r, "OUTPUT")),
      ];

      const orderBody = {
        productName: values._productName,
        subOrderItemBody: subOrderItems,
        note: values._note,
      };

      try {
        const result = await addSubOrderProduct(orderBody);
        if (result) {
          resetValues();
          setAlertData({ type: "success", message: "Sub Order Product Created Successfully..." });
          setAlert(true);
        } else {
          setAlertData({ type: "error", message: "Failed to create sub order product..." });
          setAlert(true);
        }
      } catch (e) {
        setAlertData({ type: "error", message: "Something went wrong..." });
        setAlert(true);
      }
    } else {
      setAlertData({ type: "error", message: "Please fix the validation errors before submitting" });
      setAlert(true);
    }
  };

  // ── category table renderer ──────────────────────────────────────────────
  const renderCategoryTable = (categoryRows, onRemove) => (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell><strong>Category</strong></TableCell>
          <TableCell align="right"><strong>Actions</strong></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {categoryRows.map((row) => (
          <TableRow key={row.importCategoryId}>
            <TableCell>{row.item}</TableCell>
            <TableCell align="right">
              <Tooltip title="View import items in this category">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleViewCategoryItems(row.importCategoryId, row.item)}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton size="small" color="error" onClick={() => onRemove(row.importCategoryId)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const inputItemRows = selectedInputRows.filter((r) => r.importId != null);
  const inputCatRows = selectedInputRows.filter((r) => r.importCategoryId != null);
  const outputItemRows = selectedOutputRows.filter((r) => r.importId != null);
  const outputCatRows = selectedOutputRows.filter((r) => r.importCategoryId != null);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={11} sx={12}>
        <Typography mt={1} variant="h6"> Add Sub Product </Typography>
      </Grid>
      <Grid item xs={12} sm={1} sx={12}>
        <Tooltip title="Close">
          <IconButton onClick={() => setOpenForm(false)} aria-label="close" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Grid>

      <Grid item xs={12} sm={3} sx={12}>
        <InputField
          name="_productName"
          value={values._productName}
          onChange={(event, newInputValue) => handleChange(event, newInputValue)}
          type="text"
          label="Sub Product Name"
        />
      </Grid>
      <Grid item xs={12} sm={12} sx={12} />

      {/* ── Input section ── */}
      <Grid item xs={12}>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <Button
              onClick={() => setOpenInputTable(true)}
              variant="outlined"
              style={{ borderColor: error._inputItems ? "#f44336" : undefined }}
            >
              Select Input Items
            </Button>
          </Grid>
          <Grid item>
            <Button
              onClick={() => setOpenInputCategoryTable(true)}
              variant="outlined"
              style={{ borderColor: error._inputItems ? "#f44336" : undefined }}
            >
              Select Input Categories
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        {inputItemRows.length > 0 && (
          <BillWritableTable
            columns={itemColumns}
            rows={inputItemRows}
            setRows={(updated) =>
              setSelectedInputRows([...updated, ...inputCatRows])
            }
            page={writeable_page}
            setPage={() => {}}
            rowsPerPage={writeable_rowsPerPage}
            tablePagin={false}
            setRowsPerPage={() => {}}
          />
        )}
        {inputCatRows.length > 0 && renderCategoryTable(inputCatRows, removeInputCategoryRow)}
        {error._inputItems && (
          <Typography variant="caption" style={{ color: "red", fontSize: 14, fontWeight: "bold" }}>
            {error._inputItems}
          </Typography>
        )}
      </Grid>

      {/* ── Output section ── */}
      <Grid item xs={12}>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <Button
              onClick={() => setOpenOutputTable(true)}
              variant="outlined"
              style={{ borderColor: error._outputItems ? "#f44336" : undefined }}
            >
              Select Output Items
            </Button>
          </Grid>
          <Grid item>
            <Button
              onClick={() => setOpenOutputCategoryTable(true)}
              variant="outlined"
              style={{ borderColor: error._outputItems ? "#f44336" : undefined }}
            >
              Select Output Categories
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        {outputItemRows.length > 0 && (
          <BillWritableTable
            columns={itemColumns}
            rows={outputItemRows}
            setRows={(updated) =>
              setSelectedOutputRows([...updated, ...outputCatRows])
            }
            page={writeable_page}
            setPage={() => {}}
            rowsPerPage={writeable_rowsPerPage}
            tablePagin={false}
            setRowsPerPage={() => {}}
          />
        )}
        {outputCatRows.length > 0 && renderCategoryTable(outputCatRows, removeOutputCategoryRow)}
        {error._outputItems && (
          <Typography variant="caption" style={{ color: "red", fontSize: 14, fontWeight: "bold" }}>
            {error._outputItems}
          </Typography>
        )}
      </Grid>

      {error._duplicateItems && (
        <Grid item xs={12}>
          <Typography align="left" variant="caption" style={{ color: "red", fontSize: 16, fontWeight: "bold", paddingLeft: 40 }}>
            {error._duplicateItems}
          </Typography>
        </Grid>
      )}

      <Grid item xs={12}>
        <InputField
          name="_note"
          value={values._note}
          onChange={(event, newInputValue) => handleChange(event, newInputValue)}
          type="text"
          label="Note"
          multiline={true}
        />
      </Grid>

      <Grid item xs={12}>
        {displayAlert && <FormAlert type={alertData.type} message={alertData.message} />}
      </Grid>

      <Grid item xs={12}>
        <Grid container justifyContent="space-between">
          <Grid item xs={12} sm={4}>
            <Button fullWidth variant="contained" onClick={submitValue}>Create</Button>
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button onClick={resetValues} variant="outlined">Reset</Button>
          </Grid>
        </Grid>
      </Grid>

      {/* ── Import item popups ── */}
      {openInputTable && (
        <ItemSelectingPopup
          setOpenTable={setOpenInputTable}
          openTable={openInputTable}
          handleCloseTable={handleCloseInputTable}
          setSelectedItems={setSelectedInputItems}
          selectedItems={selectedInputItems}
          setRows={setRows}
          rows={rows}
        />
      )}
      {openOutputTable && (
        <ItemSelectingPopup
          setOpenTable={setOpenOutputTable}
          openTable={setOpenOutputTable}
          handleCloseTable={handleCloseOutputTable}
          setSelectedItems={setSelectedOutputItems}
          selectedItems={selectedOutputItems}
          setRows={setRows}
          rows={rows}
          isOneChoise={true}
        />
      )}

      {/* ── Import category popups ── */}
      {openInputCategoryTable && (
        <Im_CategorySelectingPopup
          setOpenCatgoryTable={setOpenInputCategoryTable}
          openCatgoryTable={openInputCategoryTable}
          handleCloseCatgoryTable={handleCloseInputCategoryTable}
          setSelectedCatgory={setSelectedInputCategories}
          selectedCatgory={selectedInputCategories}
          catgoryRows={inputCategoryRows}
          setCatgoryRows={setInputCategoryRows}
        />
      )}
      {openOutputCategoryTable && (
        <Im_CategorySelectingPopup
          setOpenCatgoryTable={setOpenOutputCategoryTable}
          openCatgoryTable={openOutputCategoryTable}
          handleCloseCatgoryTable={handleCloseOutputCategoryTable}
          setSelectedCatgory={setSelectedOutputCategories}
          selectedCatgory={selectedOutputCategories}
          catgoryRows={outputCategoryRows}
          setCatgoryRows={setOutputCategoryRows}
        />
      )}

      {/* ── Category items view dialog ── */}
      <CategoryItemsViewDialog
        open={categoryViewOpen}
        onClose={() => setCategoryViewOpen(false)}
        categoryName={categoryViewName}
        items={categoryViewItems}
      />
    </Grid>
  );
}

export default SubOrderProductCreate;
